/**
 * Cycle Item Drawer Component
 * 
 * Complete drawer solution for adding/editing cycles including:
 * - Form component for cycle name and weight
 * - Automatic alphabetic code assignment (A, B, C...)
 * - Form validation
 */

import React, { useState, useEffect } from 'react';
import { Button, Form, Input, InputNumber, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../components/common/ButtonGroup';
import { RootState } from '@/store';
import { useSelector } from 'react-redux';

// ========== TYPE DEFINITIONS ==========

export interface CycleFormData {
    id?: string; // Optional id for edit mode
    cycleCode?: string; // Auto-generated for add mode, read-only for edit mode
    cycleName: string;
    cycleWeight: number;
}

export interface CycleItemDrawerProps {
    open: boolean;
    mode: 'add' | 'edit';
    initialValues?: Partial<CycleFormData>;
    nextCycleCode?: string; // For add mode: the next alphabetic code (A, B, C...)
    onClose: () => void;
    onSubmit: (values: CycleFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  .info-icon {
    color: #9CA3AF;
    font-size: 14px;
    cursor: help;

    &:hover {
      color: #6B7280;
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  height: 44px;
  border-radius: 8px !important;

  .ant-input-number-input {
    height: 42px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.005em;
    color: #6B7280;

    &::placeholder {
      color: #9CA3AF;
    }
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

const CharacterCounter = styled.div<{ $isNearLimit?: boolean }>`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: ${props => props.$isNearLimit ? '#EF4444' : '#9CA3AF'};
  margin-top: 4px;
  text-align: right;
`;

// ========== MAIN COMPONENT ==========

const CycleItemDrawer: React.FC<CycleItemDrawerProps> = ({
    open,
    mode,
    initialValues,
    nextCycleCode,
    onClose,
    onSubmit,
}) => {
    // Form states
    const [form] = Form.useForm();
    const [cycleCode, setCycleCode] = useState('');
    const [cycleName, setCycleName] = useState('');
    const [cycleWeight, setCycleWeight] = useState<number | undefined>(undefined);
    const { isAdmin } = useSelector((state: RootState) => state.auth);

    // UI states
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    // Reset form when initialValues change
    useEffect(() => {
        if (mode === 'edit' && initialValues) {
            // Edit mode - load existing values
            setCycleCode(initialValues.cycleCode || '');
            setCycleName(initialValues.cycleName || '');
            setCycleWeight(initialValues.cycleWeight);
            form.setFieldsValue({
                cycleCode: initialValues.cycleCode,
                cycleName: initialValues.cycleName,
                cycleWeight: initialValues.cycleWeight,
            });
        } else {
            // Add mode - reset to empty
            resetForm();
            // Set the next cycle code for display
            if (nextCycleCode) {
                setCycleCode(`${nextCycleCode}00`);
                form.setFieldsValue({
                    cycleCode: `${nextCycleCode}00`,
                });
            }
        }
    }, [open, mode, initialValues, nextCycleCode]);

    // Watch for field changes and validate
    useEffect(() => {
        const checkFormValidation = () => {
            const hasName = !!cycleName && cycleName.trim() !== '';
            const hasWeight = cycleWeight != null && cycleWeight >= 0;

            const isValid = hasName && hasWeight;
            setIsFormValid(isValid);
        };

        checkFormValidation();
    }, [cycleName, cycleWeight]);

    const resetForm = () => {
        setCycleCode('');
        setCycleName('');
        setCycleWeight(undefined);
        form.resetFields();
    };

    const handleClose = () => {
        setIsFormValid(false);
        resetForm();
        onClose();
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            const formData: CycleFormData = {
                cycleName,
                cycleWeight: cycleWeight ?? 0,
            };

            // Include id and cycleCode for edit mode
            if (mode === 'edit' && initialValues) {
                formData.id = initialValues.id;
            }

            await onSubmit(formData);
            handleClose();
        } catch (error) {
            console.error("Form submission failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const drawerTitle = mode === 'add' ? 'Add Cycle' : 'Edit Cycle';

    return (
        <RightContentDrawer
            open={open}
            title={drawerTitle}
            width={572}
            onClose={handleClose}
            footer={
                <ButtonGroup>
                    <Button
                        type="default"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleFormSubmit}
                        loading={loading}
                        disabled={!isFormValid || !isAdmin}
                    >
                        {mode === 'add' ? 'Add Cycle' : 'Save Changes'}
                    </Button>
                </ButtonGroup>
            }
        >
            <StyledForm
                id="cycle-form"
                form={form}
                layout="vertical"
                requiredMark={false}
            >
                {/* Cycle Code (Read-only, auto-generated) */}
                <Form.Item name="cycleCode">
                    <div>
                        <FormLabel>Cycle Code</FormLabel>
                        <StyledInput
                            disabled
                            value={cycleCode}
                            placeholder="Auto-generated"
                            style={{ height: 44, borderRadius: 8 }}
                        />
                        <InfoText>
                            {mode === 'add'
                                ? 'Code will be automatically assigned based on the next available letter (A, B, C...)'
                                : 'Cycle code cannot be changed'}
                        </InfoText>
                    </div>
                </Form.Item>

                {/* Cycle Name */}
                <Form.Item
                    name="cycleName"
                    rules={[
                        { required: true, message: 'Please enter a cycle name' },
                        { whitespace: true, message: 'Cycle name cannot be empty' },
                    ]}
                >
                    <div>
                        <FormLabel>Cycle Name</FormLabel>
                        <StyledInput
                            value={cycleName}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Enforce maxLength of 100 characters
                                if (value.length <= 100) {
                                    setCycleName(value);
                                }
                            }}
                            placeholder="Enter cycle name"
                            maxLength={100}
                            style={{ height: 44, borderRadius: 8 }}
                        />
                        <CharacterCounter $isNearLimit={cycleName.length >= 90}>
                            {cycleName.length}/100
                        </CharacterCounter>
                    </div>
                </Form.Item>

                {/* Cycle Weight */}
                <Form.Item
                    name="cycleWeight"
                    rules={[
                        {
                            validator: (_, value) => {
                                if (value === null || value === undefined || value === '') {
                                    return Promise.reject('Please enter a cycle weight');
                                }
                                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                                if (isNaN(numValue)) {
                                    return Promise.reject('Weight must be a number');
                                }
                                if (numValue < 0) {
                                    return Promise.reject('Weight must be a positive number');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <div>
                        <FormLabel>
                            Cycle Weight
                            <Tooltip title="This weight represents the Cycle's impact on the overall audit. All Cycle weights must sum to 100%">
                                <InfoCircleOutlined className="info-icon" />
                            </Tooltip>
                        </FormLabel>
                        <StyledInputNumber
                            type="number"
                            step={1}
                            precision={0}
                            value={cycleWeight}
                            onChange={(value) => {
                                if (value === null || value === undefined) {
                                    setCycleWeight(undefined);
                                } else {
                                    setCycleWeight(typeof value === 'string' ? parseFloat(value) : value);
                                }
                            }}
                            placeholder="Enter weight of Cycle as % of Total Evaluation"
                            controls={false}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        />
                    </div>
                </Form.Item>
            </StyledForm>
        </RightContentDrawer>
    );
};

export default CycleItemDrawer;

